/**
 * exportTableauAD.js
 * Utilitaire pour exporter le tableau "Indemnisation des ayants droit de la victime décédée"
 * en Excel (.xlsx) ou PDF paysage (.pdf)
 *
 * Colonnes et données alignées sur AjouterOffre.jsx / dealWithOffer
 *
 * Dépendances :
 *   npm install xlsx jspdf jspdf-autotable
 */

import {
    frenchDate,
    getAgeFromSinistre,
    getPourcentageRepartition,
    getPrejudiceMoralBeneficiaireKey,
    getPrixRente,
} from '../../../../../../variables/functions';
import { displayNum, displayNumbers } from '../../../../../../variables/numbersFunctions';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isPartnerQualite = (qualite = "") =>
    (qualite || "").toUpperCase().startsWith("CONJOINT");

/**
 * Retourne la clef PM brute (entier, ex: 30) d'un ayant droit,
 * en traitant les concubins comme enfant majeur/mineur selon l'âge.
 */
const getClefPMRaw = (qualite, dateNaissance, dateSinistre) => {
    if ((qualite || "").startsWith("CONCUBIN")) {
        const age = getAgeFromSinistre(dateNaissance, dateSinistre);
        return age > 18
            ? getPrejudiceMoralBeneficiaireKey("ENFANT MAJEUR")
            : getPrejudiceMoralBeneficiaireKey("ENFANT MINEUR");
    }
    return getPrejudiceMoralBeneficiaireKey(qualite);
};

/**
 * Garantit que la valeur passée à doc.text() est toujours une string.
 */
const dn = (v) => {
    const result = displayNum(v);
    return (result !== null && result !== undefined) ? String(result) : "0";
};

// ─── Calcul des données (même logique que dealWithOffer) ─────────────────────

const buildData = async (offre, sinistre) => {
    const taux = sinistre?.tauxResponsabilite ?? 100;
    const victime = offre?.victime;
    const ayantsDroit = victime?.ayantsDroit ?? [];
    const smig = offre?.smig ?? 52500;

    const situationFamiliale = victime?.situationFamilialeDefunt;
    const revenuMensuel = victime?.revenuProfessionelMensuelAvantAccident ?? 0;

    // ── Revenus à capitaliser par catégorie ────────────────────────────────
    const nbreCjts = ayantsDroit.filter((ad) => isPartnerQualite(ad.qualite)).length;
    const conjointKey = getPourcentageRepartition(situationFamiliale, "conjoint");
    const revCapCjt = nbreCjts
        ? ((revenuMensuel * conjointKey * 12) / 100) / nbreCjts
        : 0;

    const nbreAscendants = ayantsDroit.filter(
        (ad) => ad.qualite?.toUpperCase().startsWith("PERE") || ad.qualite?.toUpperCase().startsWith("MERE")
    ).length;
    const ascendantsKey = getPourcentageRepartition(situationFamiliale, "ascendant");
    const revCapAscendants = nbreAscendants
        ? ((revenuMensuel * ascendantsKey * 12) / 100) / nbreAscendants
        : 0;

    const nbreOrphelins = ayantsDroit.filter(
        (ad) => ad.qualite?.toUpperCase().startsWith("ENFANT MINEUR") || ad.qualite?.toUpperCase().startsWith("ENFANT_MINEUR")
    ).length;
    const orphelinsKey = getPourcentageRepartition(situationFamiliale, "enfant");
    const revCapOrphelins = nbreOrphelins
        ? (((revenuMensuel || smig) * orphelinsKey * 12) / 100) / nbreOrphelins
        : 0;

    const tPe = revCapCjt * nbreCjts + revCapAscendants * nbreAscendants + revCapOrphelins * nbreOrphelins;
    const plafEco = 12 * smig * 85;

    // ── Calcul PM total pour plafonnement ──────────────────────────────────
    let tpm = 0;
    let tPmV = 0;
    ayantsDroit.forEach((ad) => {
        const clefPM = getClefPMRaw(ad.qualite, ad.dateNaissance, sinistre?.dateSinistre) / 100;
        const plaf = isPartnerQualite(ad.qualite) ? smig * 6 * 12 : smig * 20 * 12;
        const pm = Math.min(smig * 12 * clefPM, plaf);
        if (isPartnerQualite(ad.qualite) && ad.genre?.toUpperCase() === "FEMININ") tPmV += pm;
        tpm += pm;
    });

    // ── Ligne par ayant droit ──────────────────────────────────────────────
    const rows = await Promise.all(
        ayantsDroit.map(async (ad, index) => {
            const age = getAgeFromSinistre(ad.dateNaissance, sinistre?.dateSinistre);

            const adType = (ad.qualite === "PERE" || ad.qualite === "MERE")
                ? "ascendant"
                : ad.qualite?.startsWith("ENFANT")
                    ? (ad.typeOrphelin === "ORPHELIN DOUBLE" ? "orphelinDouble" : "enfant")
                    : isPartnerQualite(ad.qualite) ? "conjoint" : null;

            const clefEPct = adType ? getPourcentageRepartition(situationFamiliale, adType) : 0;
            const clefPMRaw = getClefPMRaw(ad.qualite, ad.dateNaissance, sinistre?.dateSinistre);
            const clefPM = clefPMRaw / 100;

            const rac = (ad.qualite === "PERE" || ad.qualite === "MERE")
                ? revCapAscendants
                : ad.qualite?.startsWith("ENFANT")
                    ? revCapOrphelins
                    : isPartnerQualite(ad.qualite) ? revCapCjt : 0;

            const quality = (ad.qualite?.toUpperCase().startsWith("PERE") || ad.qualite?.toUpperCase().startsWith("MERE"))
                ? 100
                : ad.qualite?.startsWith("ENFANT") ? 25
                    : isPartnerQualite(ad.qualite) ? 100 : 0;

            const prixRente = await getPrixRente(age, ad.genre, quality);

            const cumulPM = isPartnerQualite(ad.qualite) && ad.genre?.toUpperCase() === "FEMININ" ? tPmV : tpm;
            const plaf = isPartnerQualite(ad.qualite) && ad.genre?.toUpperCase() === "FEMININ"
                ? smig * 6 * 12
                : smig * 20 * 12;

            const pm = cumulPM > plaf
                ? (plaf * Math.min(smig * 12 * clefPM, plaf)) / cumulPM
                : Math.min(smig * 12 * clefPM, plaf);

            const pe = tPe > plafEco
                ? (plafEco * rac * prixRente) / tPe
                : rac * prixRente;

            const tot = pm + pe;
            const totPartage = Math.round(tot * taux / 100);

            return {
                nomPrenom: `${ad.nom || ""} ${ad.prenom || ""}`.trim(),
                sexe: ad.genre || "",
                qualite: ad.qualite || "",
                dateNaiss: ad.dateNaissance || "",
                age,
                prixRente: prixRente ?? 0,
                clefEPct,       // ex: 50  → affiché "50 %"
                clefPMPct: clefPMRaw, // ex: 30  → affiché "30 %"
                rac,
                pe,
                pm,
                tot,
                totPartage,
            };
        })
    );

    const totalPE = rows.reduce((s, r) => s + r.pe, 0);
    const totalPM = rows.reduce((s, r) => s + r.pm, 0);
    const totalIndem = offre?.totalIndemnites ?? rows.reduce((s, r) => s + r.tot, 0);
    const totalPartage = Math.round(totalIndem * taux / 100);

    return { rows, totalPE, totalPM, totalIndem, totalPartage, taux, smig };
};

// ─── Métadonnées (2 colonnes) ─────────────────────────────────────────────────

const buildMeta = (offre, sinistre, smig) => {
    const v = offre?.victime;
    const ayantsDroit = v?.ayantsDroit ?? [];

    const col1 = [
        ["SINISTRE", sinistre?.numSinistre || sinistre?.numeroSinistreM || ""],
        ["DATE DE SURVENANCE", frenchDate(sinistre?.dateSinistre) || ""],
        ["VICTIME DÉCÉDÉE", `${v?.personne?.nom || ""} ${v?.personne?.prenom || ""}`.trim()],
        ["SITUATION FAMILIALE", v?.situationFamilialeDefunt || ""],
        ["TYPE D'ORPHELIN", v?.typeOrphelin || ""],
        ["REVENU ANNUEL", dn((v?.revenuProfessionelMensuelAvantAccident ?? 0) * 12)],
        ["SMIG ANNUEL", dn(smig * 12)],
    ];

    const col2 = [
        ["NOMBRE D'ENFANTS MINEURS", ayantsDroit.filter(ad => ["ENFANT MINEUR", "ENFANT_MINEUR"].includes(ad.qualite?.toUpperCase())).length],
        ["NOMBRE D'ENFANTS MAJEURS", ayantsDroit.filter(ad => ["ENFANT MAJEUR", "ENFANT_MAJEUR"].includes(ad.qualite?.toUpperCase())).length],
        ["NOMBRE DE CONJOINTS", ayantsDroit.filter(ad => isPartnerQualite(ad.qualite)).length],
        ["NOMBRE D'ASCENDANTS", ayantsDroit.filter(ad => ["PERE", "MERE"].includes(ad.qualite?.toUpperCase())).length],
        ["NOMBRE DE FRÈRES ET SŒURS", ayantsDroit.filter(ad => ["FRERE", "SOEUR"].includes(ad.qualite?.toUpperCase())).length],
        ["PLAFOND PRÉJ. MORAL CONJOINT(ES)", dn(6 * smig * 12)],
        ["PLAFOND PRÉJUDICE MORAL TOTAL", dn(20 * smig * 12)],
        ["PLAFOND PRÉJUDICE ÉCONOMIQUE", dn(85 * smig * 12)],
        ["TAUX DE RESPONSABILITÉ", `${sinistre?.tauxResponsabilite ?? 100} %`],
    ];

    return { col1, col2 };
};

// ═══════════════════════════════════════════════════════════════════════════
//  EXPORT EXCEL
// ═══════════════════════════════════════════════════════════════════════════

export const exportExcel = async (offre, sinistre) => {
    const { rows, totalPE, totalPM, totalIndem, totalPartage, taux, smig } =
        await buildData(offre, sinistre);

    const { col1, col2 } = buildMeta(offre, sinistre, smig);
    const wb = XLSX.utils.book_new();
    const wsData = [];

    // ── Titre ──────────────────────────────────────────────────────────────
    wsData.push(["INDEMNISATION DES PRÉJUDICES SUBIS PAR LES AYANTS DROIT DE LA VICTIME DÉCÉDÉE"]);
    wsData.push([]);

    // ── Section infos (2 colonnes côte à côte) ─────────────────────────────
    const maxInfoRows = Math.max(col1.length, col2.length);
    for (let i = 0; i < maxInfoRows; i++) {
        const [k1 = "", v1 = ""] = col1[i] ?? [];
        const [k2 = "", v2 = ""] = col2[i] ?? [];
        wsData.push([k1, String(v1), "", k2, String(v2)]);
    }
    wsData.push([]);

    // ── En-têtes du tableau principal ──────────────────────────────────────
    wsData.push([
        "NOM & PRÉNOMS",
        "SEXE",
        "QUALITÉ",
        "DATE NAISSANCE",
        "ÂGE",
        "COEFF. CAPITALISATION",
        "CLÉ RÉPART. ÉCON. (%)",
        "CLÉ RÉPART. MORAL (%)",
        "REVENU À CAPITALISER",
        "PRÉJUDICE ÉCONOMIQUE",
        "PRÉJUDICE MORAL",
        "TOTAL INDEM. 100 %",
        `TOTAL APRÈS PARTAGE ${taux} %`,
    ]);

    // ── Lignes ayants droit ────────────────────────────────────────────────
    rows.forEach((r) =>
        wsData.push([
            r.nomPrenom,
            r.sexe,
            r.qualite,
            r.dateNaiss,
            r.age,
            r.prixRente,
            r.clefEPct,
            r.clefPMPct,
            r.rac,
            r.pe,
            r.pm,
            r.tot,
            r.totPartage,
        ])
    );

    // ── Ligne totaux ───────────────────────────────────────────────────────
    wsData.push([
        "TOTAL DES INDEMNITÉS", "", "", "", "", "", "", "", "",
        totalPE, totalPM, totalIndem, totalPartage,
    ]);
    wsData.push([]);

    // ── Récapitulatif bas ──────────────────────────────────────────────────
    const fraisFun = offre?.montantFraisFuneraires ?? 0;
    const fraisMed = offre?.montantFraisMedicaux ?? 0;
    const provisions = 0; // à adapter si stocké dans offre

    wsData.push(["FRAIS FUNÉRAIRES JUSTIFIÉS", "", "", "", "", "", "", "", "", "", "", fraisFun, Math.round(fraisFun * taux / 100)]);
    wsData.push(["FRAIS MÉDICAUX JUSTIFIÉS (+)", "", "", "", "", "", "", "", "", "", "", fraisMed, ""]);
    wsData.push(["PROVISIONS À DÉDUIRE (–)", "", "", "", "", "", "", "", "", "", "", provisions, ""]);
    wsData.push(["TOTAL À PAYER", "", "", "", "", "", "", "", "", "", "", "", offre?.totalAPayer ?? ""]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Largeurs de colonnes ───────────────────────────────────────────────
    ws["!cols"] = [
        { wch: 34 }, { wch: 22 }, { wch: 3 }, { wch: 34 }, { wch: 22 }, // colonnes infos (lignes paires)
        { wch: 8 }, { wch: 18 }, { wch: 14 }, { wch: 5 },
        { wch: 16 }, { wch: 16 }, { wch: 20 },
        { wch: 20 }, { wch: 22 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Tableau AD");

    const numSin = sinistre?.numSinistre || sinistre?.numeroSinistreM || "sinistre";
    XLSX.writeFile(wb, `Tableau_AD_${numSin}.xlsx`);
};

// ═══════════════════════════════════════════════════════════════════════════
//  EXPORT PDF  (mode PAYSAGE A4)
// ═══════════════════════════════════════════════════════════════════════════

export const exportPDF = async (offre, sinistre) => {
    const { rows, totalPE, totalPM, totalIndem, totalPartage, taux, smig } =
        await buildData(offre, sinistre);

    const { col1, col2 } = buildMeta(offre, sinistre, smig);

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const PAGE_W = 297;
    const MARGIN = 10;
    const CONTENT = PAGE_W - MARGIN * 2;

    // ── Titre ────────────────────────────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(
        "INDEMNISATION DES PRÉJUDICES SUBIS PAR LES AYANTS DROIT DE LA VICTIME DÉCÉDÉE",
        PAGE_W / 2, 13, { align: "center" }
    );

    // ── Section infos (tableau 4 colonnes : label | valeur | label | valeur) ─
    const maxLen = Math.max(col1.length, col2.length);
    const infoRows = [];
    for (let i = 0; i < maxLen; i++) {
        const [k1 = "", v1 = ""] = col1[i] ?? [];
        const [k2 = "", v2 = ""] = col2[i] ?? [];
        infoRows.push([
            { content: k1, styles: { fontStyle: "bold", fillColor: [235, 235, 248] } },
            { content: String(v1) },
            { content: k2, styles: { fontStyle: "bold", fillColor: [235, 235, 248] } },
            { content: String(v2) },
        ]);
    }

    autoTable(doc, {
        startY: 18,
        margin: { left: MARGIN, right: MARGIN },
        tableWidth: CONTENT,
        body: infoRows,
        columnStyles: {
            0: { cellWidth: 52 },
            1: { cellWidth: 84 },
            2: { cellWidth: 62 },
            3: { cellWidth: 79 },
        },
        bodyStyles: { fontSize: 7, cellPadding: 1.8, valign: "middle" },
        theme: "plain",
        tableLineColor: [210, 210, 220],
        tableLineWidth: 0.15,
    });

    const yStart = doc.lastAutoTable.finalY + 5;

    // ── Tableau principal ────────────────────────────────────────────────────
    const colStyles = {
        0: { cellWidth: 28 },
        1: { cellWidth: 10, halign: "center" },
        2: { cellWidth: 20 },
        3: { cellWidth: 17, halign: "center" },
        4: { cellWidth: 7, halign: "center" },
        5: { cellWidth: 14, halign: "right" },
        6: { cellWidth: 11, halign: "center" },
        7: { cellWidth: 11, halign: "center" },
        8: { cellWidth: 22, halign: "right" },
        9: { cellWidth: 25, halign: "right" },
        10: { cellWidth: 22, halign: "right" },
        11: { cellWidth: 26, halign: "right" },
        12: { cellWidth: 26, halign: "right" },
    };

    const tableRows = rows.map((r) => [
        String(r.nomPrenom || ""),
        String(r.sexe || ""),
        String(r.qualite || ""),
        String(r.dateNaiss || ""),
        String(r.age ?? ""),
        dn(r.prixRente),
        `${r.clefEPct} %`,
        `${r.clefPMPct} %`,
        dn(r.rac),
        dn(r.pe),
        dn(r.pm),
        dn(r.tot),
        dn(r.totPartage),
    ]);

    // Ligne totaux
    tableRows.push([
        {
            content: "TOTAL DES INDEMNITÉS", colSpan: 9,
            styles: { fontStyle: "bold", fillColor: [36, 33, 102], textColor: 255 },
        },
        { content: dn(totalPE), styles: { fontStyle: "bold", fillColor: [36, 33, 102], textColor: 255, halign: "right" } },
        { content: dn(totalPM), styles: { fontStyle: "bold", fillColor: [36, 33, 102], textColor: 255, halign: "right" } },
        { content: dn(totalIndem), styles: { fontStyle: "bold", fillColor: [36, 33, 102], textColor: 255, halign: "right" } },
        { content: dn(totalPartage), styles: { fontStyle: "bold", fillColor: [36, 33, 102], textColor: 255, halign: "right" } },
    ]);

    autoTable(doc, {
        startY: yStart,
        margin: { left: MARGIN, right: MARGIN },
        tableWidth: CONTENT,
        head: [[
            "NOM & PRÉNOMS", "SEXE", "QUALITÉ", "DATE NAISS.", "ÂGE",
            "COEFF.\nCAPITALIS.",
            "CLÉ\nÉCON.", "CLÉ\nMORAL",
            "REV. À\nCAPITALISER",
            "PRÉJUDICE\nÉCONOMIQUE", "PRÉJUDICE\nMORAL",
            "TOTAL INDEM.\n100 %", `TOTAL APRÈS\nPARTAGE ${taux} %`,
        ]],
        body: tableRows,
        columnStyles: colStyles,
        headStyles: {
            fillColor: [36, 33, 102],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 6.5,
            halign: "center",
            valign: "middle",
            minCellHeight: 12,
        },
        bodyStyles: { fontSize: 6.5, valign: "middle" },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        theme: "grid",
    });

    // ── Récapitulatif bas de page ────────────────────────────────────────────
    const fraisFun = offre?.montantFraisFuneraires ?? 0;
    const fraisMed = offre?.montantFraisMedicaux ?? 0;
    const totalAPayer = offre?.totalAPayer ?? 0;

    const yBottom = doc.lastAutoTable.finalY + 5;
    doc.setFontSize(7.5);

    const addSummaryRow = (label, value, y, bold = false) => {
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.text(label, PAGE_W - MARGIN - 88, y);
        doc.text(dn(value), PAGE_W - MARGIN, y, { align: "right" });
    };

    addSummaryRow("Frais funéraires justifiés :", fraisFun, yBottom);
    addSummaryRow(`Frais funéraires à rembourser (${taux} %) :`, Math.round(fraisFun * taux / 100), yBottom + 5);
    addSummaryRow("Frais médicaux justifiés (+) :", fraisMed, yBottom + 10);
    addSummaryRow("Provisions à déduire (–) :", 0, yBottom + 15);

    // Trait séparateur
    doc.setDrawColor(36, 33, 102);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, yBottom + 19, PAGE_W - MARGIN, yBottom + 19);

    addSummaryRow("TOTAL À PAYER :", totalAPayer, yBottom + 24, true);

    const numSin = sinistre?.numSinistre || sinistre?.numeroSinistreM || "sinistre";
    doc.save(`Tableau_AD_${numSin}.pdf`);
};

// ─── Point d'entrée unique ────────────────────────────────────────────────────

/**
 * @param {object}         offre
 * @param {object}         sinistre
 * @param {'excel'|'pdf'}  format
 */
export const exportTableauAD = (offre, sinistre, format) => {
    if (format === "excel") return exportExcel(offre, sinistre);
    return exportPDF(offre, sinistre);
};